<template>
  <AkInputFrame
    :size="null"
    :disabled="disabled"
    :has-error="hasError"
    class="review-input ds-relative"
    :class="`review-input--${type}`"
    :data-count="valueLength"
  >
    <component
      :is="type"
      ref="inputRef"
      :class="controlClasses"
      :value="modelValue"
      :placeholder="placeholder"
      :maxlength="maxLength"
      :disabled="disabled"
      @input.stop="handleInput"
      @blur="$emit('blur')"
    >
      {{ modelValue }}
    </component>
  </AkInputFrame>
</template>

<script lang="ts">
import { AkInputFrame } from '@ankorstore/design-system';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'AkInput',
  components: { AkInputFrame },
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },
  props: {
    type: {
      type: String,
      default: 'input',
      validator(value: string) {
        return ['input', 'textarea'].includes(value);
      },
    },
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: null,
    },
    maxLength: {
      type: Number,
      default: Infinity,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['blur', 'maxLengthReached', 'update:modelValue'],
  computed: {
    controlClasses(): string[] {
      const classes = ['control', 'ds-w-full', 'ds-outline-none', 'ds-text-sm', 'ds-bg-transparent'];

      if (this.maxLength) {
        classes.push('control--with-max-length');
      }

      return classes;
    },
    valueLength(): string {
      return `${this.modelValue?.length}/${this.maxLength}`;
    },
  },
  methods: {
    focus() {
      (this.$refs['inputRef'] as HTMLInputElement).focus();
    },
    handleInput(e) {
      const value = e.target.value;
      if (this.maxLength && value.length <= this.maxLength) {
        this.$emit('update:modelValue', value);
      } else {
        this.$emit('maxLengthReached');
      }
    },
  },
});
</script>

<style scoped lang="scss">
.review-input {
  .control {
    @apply ds-px-3 ds-py-2;
  }
  &::after {
    @apply ds-absolute ds-text-sm ds-text-neutral-600 ds-right-2;
    content: attr(data-count);
    bottom: 10px;
  }
}

// Private to this component. These were previously emitted as `ak-input--input` and
// `ak-input--textarea`, squatting a namespace the design system now owns via AkInputFrame.
.review-input--input {
  .control--with-max-length {
    @apply ds-pr-8;
  }
}

.review-input--textarea {
  .control {
    @apply ds-pr-8 ds-pb-6 ds-resize-y;
    min-height: calc(3rem + 2rem * 1.25);
  }
}
</style>
